import { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders modal content into document.body.
 *
 * Modals declared inline get trapped by any ancestor that creates a stacking
 * context (a transform, a filter, an opacity, a positioned parent with its own
 * z-index), so a z-50 overlay could still end up painted under the side panel,
 * a sticky table header or a row menu. Portalling to the body sidesteps that,
 * and every modal then shares one layer.
 *
 * App z-index scale:
 *   z-30          side panel
 *   MODAL_Z       modals (this component)
 *   DROPDOWN_Z    dropdowns / suggestion lists / row menus
 *   NESTED_MODAL_Z  a modal opened from inside another modal
 */
export const MODAL_Z = 'z-[1200]';
export const DROPDOWN_Z = 'z-[1300]';
export const NESTED_MODAL_Z = 'z-[1400]';

export default function ModalPortal({ isOpen = true, onClose, children, lockScroll = true }) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    if (lockScroll) document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (lockScroll) document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose, lockScroll]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(children, document.body);
}
